import {
  useCallback,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { useRemark } from '../hooks/useRemark';
import { useSettingsStore } from '../stores/settingsStore';
import { fixMarkdownTables } from '../utils/fixMarkdownTables';

interface MarkdownRendererProps {
  content: string;
  isStreaming?: boolean;
  suppressRawFallback?: boolean;
  /** Callback when a doc reference link is clicked (file path) */
  onDocReference?: (path: string) => void;
}

function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map((child) => extractText(child)).join('');
  }

  if (typeof node === 'object' && 'props' in node) {
    const maybeProps = (node as { props?: { children?: ReactNode } }).props;
    return extractText(maybeProps?.children ?? '');
  }

  return '';
}

function CodeBlockWrapper(props: HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false);
  const rawText = useMemo(() => extractText(props.children), [props.children]);

  const handleCopy = async () => {
    if (!rawText.trim()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(rawText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <div className="group relative my-4">
      <button
        type="button"
        onClick={() => void handleCopy()}
        className="absolute right-2 top-2 z-10 rounded-md border border-input bg-card px-2 py-1 text-[11px] text-foreground opacity-0 transition group-hover:opacity-100"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>

      <pre {...props} className={`${props.className ?? ''} overflow-x-auto rounded-xl border border-input`}>
        {props.children}
      </pre>
    </div>
  );
}

function ExternalLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className={`underline decoration-ring underline-offset-2 hover:decoration-ring-light ${props.className ?? ''}`}
    >
      {props.children}
    </a>
  );
}

/**
 * Check whether an href looks like a file path (as opposed to a real URL).
 * File paths: docs/foo.md, ./README.md, ~/projects/game/bar.txt, MEMORY.md
 * Real URLs: http://..., https://..., mailto:..., #anchor, javascript:...
 */
function isFilePathHref(href: string | undefined): boolean {
  if (!href) return false;
  const trimmed = href.trim();
  // Real URLs / anchors / protocols
  if (/^(?:https?:|mailto:|tel:|#|javascript:|data:)/i.test(trimmed)) return false;
  // Must end in a known doc extension
  return FILE_PATH_PATTERN.test(trimmed);
}

/**
 * Anchor component that intercepts file-path links and routes them
 * through onDocReference instead of opening as external URLs.
 */
function makeAnchor(onDocReference?: (path: string) => void) {
  return function Anchor(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
    const { href, children } = props;

    if (onDocReference && isFilePathHref(href)) {
      const filePath = (href ?? '').trim();
      return (
        <DocReferenceLink filePath={filePath} onDocReference={onDocReference}>
          <span className="doc-ref-anchor-text">{children}</span>
        </DocReferenceLink>
      );
    }

    return <ExternalLink {...props} />;
  };
}

/**
 * Regex to detect file path references ending in .md or .txt.
 * Matches patterns like:
 *   docs/foo.md, ~/projects/game/docs/bar.md, ./README.md,
 *   MEMORY.md, path/to/file.txt, ../some/file.md
 */
const FILE_PATH_PATTERN = /^(?:~\/|\.\/|\.\.\/)?(?:[\w.@-]+\/)*[\w.@-]+\.(?:md|txt)$/;

function isFilePath(text: string): boolean {
  return FILE_PATH_PATTERN.test(text.trim());
}

function DocReferenceLink({
  filePath,
  onDocReference,
  children,
}: {
  filePath: string;
  onDocReference: (path: string) => void;
  children: ReactNode;
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDocReference(filePath);
    },
    [filePath, onDocReference],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      className="doc-ref-link"
      title={`Open ${filePath}`}
    >
      <span className="doc-ref-link-icon">📄</span>
      {children}
    </button>
  );
}

/**
 * Inline code component that detects file path references and makes them clickable.
 * Skips code inside code blocks (which have className like "language-*").
 */
function makeInlineCode(onDocReference?: (path: string) => void) {
  return function InlineCode(props: HTMLAttributes<HTMLElement>) {
    const text = extractText(props.children);

    // Code blocks (<pre><code>) get a className like "language-typescript"
    // from Shiki. Skip those — only linkify inline <code>.
    const isCodeBlock = typeof props.className === 'string' && props.className.includes('language-');

    if (!isCodeBlock && onDocReference && text && isFilePath(text)) {
      return (
        <DocReferenceLink filePath={text.trim()} onDocReference={onDocReference}>
          <code {...props}>{props.children}</code>
        </DocReferenceLink>
      );
    }

    return <code {...props}>{props.children}</code>;
  };
}

/**
 * Paragraph component that scans text nodes for bare file path references
 * (not wrapped in backticks) and makes them clickable.
 */
function makeParagraph(onDocReference?: (path: string) => void) {
  return function Paragraph(props: HTMLAttributes<HTMLParagraphElement>) {
    if (!onDocReference) {
      return <p {...props}>{props.children}</p>;
    }

    const processed = processChildrenForPaths(props.children, onDocReference);
    return <p {...props}>{processed}</p>;
  };
}

/**
 * Recursively process React children to find and linkify bare file path text.
 */
function processChildrenForPaths(
  children: ReactNode,
  onDocReference: (path: string) => void,
): ReactNode {
  if (children == null || typeof children === 'boolean') return children;

  if (typeof children === 'string') {
    return linkifyFilePathsInText(children, onDocReference);
  }

  if (typeof children === 'number') {
    return children;
  }

  if (Array.isArray(children)) {
    return children.map((child, i) => {
      const processed = processChildrenForPaths(child, onDocReference);
      // If the result is an array, wrap with key; otherwise apply key to element
      if (Array.isArray(processed)) {
        return <span key={i}>{processed}</span>;
      }
      return typeof processed === 'object' && processed !== null && 'key' in processed
        ? processed
        : <span key={i}>{processed}</span>;
    });
  }

  // Don't recurse into existing elements (code, a, etc.) — just return as-is
  return children;
}

/**
 * Split a text string on file path patterns and return an array of
 * text nodes and DocReferenceLink elements.
 */
const FILE_PATH_INLINE_PATTERN =
  /(?:^|\s)((?:~\/|\.\/|\.\.\/)?(?:[\w.@-]+\/)*[\w.@-]+\.(?:md|txt))(?=\s|[,;:!?)\]}]|$)/g;

function linkifyFilePathsInText(
  text: string,
  onDocReference: (path: string) => void,
): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  FILE_PATH_INLINE_PATTERN.lastIndex = 0;
  while ((match = FILE_PATH_INLINE_PATTERN.exec(text)) !== null) {
    const fullMatch = match[0];
    const filePath = match[1];
    const matchStart = match.index;

    // Add text before the match
    const preText = text.slice(lastIndex, matchStart);
    if (preText) parts.push(preText);

    // Add leading whitespace if the fullMatch has it
    const leadingWhitespace = fullMatch.slice(0, fullMatch.indexOf(filePath));
    if (leadingWhitespace) parts.push(leadingWhitespace);

    // Add the linked file path
    parts.push(
      <DocReferenceLink
        key={`ref-${matchStart}`}
        filePath={filePath}
        onDocReference={onDocReference}
      >
        <span className="doc-ref-inline-path">{filePath}</span>
      </DocReferenceLink>,
    );

    lastIndex = matchStart + fullMatch.length;
  }

  if (parts.length === 0) return text;

  // Add remaining text
  const remaining = text.slice(lastIndex);
  if (remaining) parts.push(remaining);

  return parts;
}

export default function MarkdownRenderer({
  content,
  isStreaming = false,
  suppressRawFallback = false,
  onDocReference,
}: MarkdownRendererProps) {
  const components = useMemo(
    () => ({
      pre: CodeBlockWrapper,
      a: makeAnchor(onDocReference),
      code: makeInlineCode(onDocReference),
      p: makeParagraph(onDocReference),
    }),
    [onDocReference],
  );

  const codeTheme = useSettingsStore((state) => state.settings.editor.codeTheme);

  // Pre-process markdown to fix malformed tables (e.g. mismatched column counts
  // in separator rows) that would otherwise render as raw pipe-delimited text.
  const fixedContent = useMemo(() => fixMarkdownTables(content), [content]);

  const { content: renderedContent, error } = useRemark(
    fixedContent,
    isStreaming ? 100 : 0,
    components,
    codeTheme,
  );

  if (error && !renderedContent) {
    if (suppressRawFallback) {
      return null;
    }

    return (
      <div>
        <div className="mb-2 text-[11px] uppercase tracking-wide text-warning opacity-80">Markdown render fallback</div>
        <pre className="whitespace-pre-wrap break-words rounded-xl border border-input bg-card px-3 py-2 text-inherit text-warning">
          {content}
        </pre>
      </div>
    );
  }

  if (!renderedContent) {
    if (suppressRawFallback) {
      return null;
    }

    return <pre className="whitespace-pre-wrap break-words text-inherit text-foreground">{content}</pre>;
  }

  return <div className="markdown-body break-words text-inherit text-foreground">{renderedContent}</div>;
}
